'use client';

import React, { useEffect, useState } from 'react';
import { FileQuestion, Home, ArrowLeft, Search } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import Link from 'next/link';

/**
 * Next.js 404 Not Found Page Component
 * Displays when a page or resource is not found
 */
export default function NotFoundPage() {
  const { t, isRTL } = useCommonTranslation();
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  const handleGoBack = () => {
    if (typeof window !== 'undefined') {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = '/dashboard';
      }
    }
    // For SSR, navigation will be handled by Next.js Link components
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <FileQuestion className="h-8 w-8 text-orange-500" />
            <Badge variant="secondary" className="flex items-center gap-1">
              <FileQuestion className="h-3 w-3" />
              404
            </Badge>
          </div>
          
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('errors.notFound.title')}
          </CardTitle>
          
          <CardDescription className="text-lg mt-2">
            {t('errors.notFound.description')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current URL Info */}
          <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
            <p className="text-sm text-orange-700 dark:text-orange-300">
              <strong>{t('errors.notFound.requestedUrl')}:</strong> {currentPath}
            </p>
          </div>

          {/* Navigation Actions */}
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={handleGoBack} className="min-w-[140px]">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
            
            <Link href="/dashboard">
              <Button variant="outline" className="min-w-[140px]">
                <Home className="h-4 w-4 mr-2" />
                {t('errors.boundary.actions.goHome')}
              </Button>
            </Link>
            
            <Link href="/products">
              <Button variant="outline" className="min-w-[140px]">
                <Search className="h-4 w-4 mr-2" />
{t('actions.view')} {t('navigation.products')}
              </Button>
            </Link>
          </div>

          {/* Helpful Links */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 text-center">
{t('errors.notFound.popularPages')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link href="/dashboard" className="p-3 text-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="text-sm font-medium">{t('navigation.dashboard')}</div>
              </Link>
              <Link href="/orders" className="p-3 text-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="text-sm font-medium">{t('navigation.orders')}</div>
              </Link>
              <Link href="/products" className="p-3 text-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="text-sm font-medium">{t('navigation.products')}</div>
              </Link>
              <Link href="/users" className="p-3 text-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="text-sm font-medium">{t('navigation.customers')}</div>
              </Link>
            </div>
          </div>

          {/* Help Text */}
          <div className="text-center text-sm text-gray-600 dark:text-gray-300">
            <p>{t('errors.notFound.supportMessage')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}